import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Divider, Input, Modal, PageHeader, Space, Select} from 'antd';
import _ from 'lodash';
import {isDefaultAuth, isAzureAuth} from '@/app/AuthUtils';

const {Option} = Select;

import {
    ApartmentOutlined,
    AuditOutlined,
    DeleteOutlined,
    EditOutlined,
    FileProtectOutlined,
    LockOutlined,
    TagsOutlined,
} from '@/icons';
import {PageGrid, RowAction} from '@/component';

import RoleSelector from '../selector/RoleSelector';

import UserResetPassModal from './UserResetPassModal';
import MultiDepartEditModal from './MultiDepartEditModal';
import SyncContactToDBModal from './SyncContactToDBModal';
import SyncContactToWecomModal from './SyncContactToWecomModal';

import UserViewModal from './UserViewModal';
import UserFormModal from './UserFormModal';
import {getColumns} from './userListColumns';
import {
    batchUpdateUserTags,
    deleteUser,
    getUserTags,
    deleteNeedUser,
    realDeleteUser,
} from '@/api/users';
import DepartUserTagSelector from '@/page/admin/selector/DepartUserTagSelector';
import ReportLineUserSelector from '@/page/admin/ReportLinePage/ReportLineUserSelector/ReportLineUserSelector';
import {addReportDepartsLine, addReportLine} from '@/api/reportLine';
import {sendBatchJourneies} from '@/api/travel';

function getDefaultSearchUrl(deptId, value, status, needDel) {
    if (isDefaultAuth)
        return `/users?s=${encodeURIComponent(value)}&status=${encodeURIComponent(
            status
        )}&deptId=${encodeURIComponent(deptId)}`;
    else
        return `/users/sso?s=${encodeURIComponent(value)}&status=${encodeURIComponent(
            status
        )}&needDel=${encodeURIComponent(needDel)}&deptId=${encodeURIComponent(deptId)}`;
}

export default function UserList({
    depart,
    editable,
    // columnKeys = isDefaultAuth
    //     ? ['avatar', 'biName', 'contact', 'state', 'wecomStatus']
    //     : ['avatar', 'name', 'orgText', 'contact', 'state', 'wecomStatus'],
    columnKeys = ['avatar', 'name', 'orgText', 'contact', 'state', 'wecomStatus'],
    columnAction,
    getSearchUrl = getDefaultSearchUrl,
}) {
    const formModalRef = useRef();
    const roleSelRef = useRef();
    const tableRef = useRef();
    const viewRef = useRef();
    const syncDBRef = useRef();
    const syncWecomRef = useRef();
    const resetPassModalRef = useRef();
    const departEditModalRef = useRef();
    const dutSelectorRef = useRef();
    const reportLineSelectorRef = useRef();

    const [pageTitle, setPageTitle] = useState(null);
    const [url, setUrl] = useState(null);
    const [searchFlag, setSearchFlag] = useState(false);
    const [allowDeparts, setAllowDeparts] = useState([]);
    const [needDel, setNeedDel] = useState('');
    const [status, setStatus] = useState(0);

    // console.log('url.....', url, depart);

    // 部门切换的时候重置相关数据
    useEffect(() => {
        if (!_.isEmpty(depart)) {
            reset2Depart();
            // onSearch('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depart]);

    function reset2Depart() {
        setNeedDel('');
        setPageTitle(depart.name);
        setUrl(`/departs/${depart.id}/users`);
        setSearchFlag(false);
    }

    function onAddClick() {
        formModalRef.current.showModal();
    }

    function onNameClick(record) {
        return () => {
            viewRef.current.showModal(record);
        };
    }

    function onModifyClick(record) {
        formModalRef.current.showModal(record.id);
    }

    function onModifyDepartsClick(record) {
        departEditModalRef.current.showModal(record.id);
    }

    let selectUserId;

    function sendBatchMessage() {
        const content = (
            <div>
                <p>确定全量发送年会消息？</p>
                <Divider />
            </div>
        );
        Modal.confirm({
            title: '警告',
            content,
            okButtonProps: {danger: true},
            onOk: () => {
                sendBatchJourneies().then(() => {
                });
            },
        });
    }

    function onModifyTagsClick(record) {
        if (!record.enableSync) {
            Modal.warn({
                title: '警告',
                content: '此用户并不同步到企业微信，故不可编辑标签',
            });
            return;
        }

        selectUserId = record.id;
        getUserTags(selectUserId).then(({data}) => {
            dutSelectorRef.current.showModal({
                initialValues: {tags: data},
            });
        });
    }

    function onTagsSelect(values) {
        const {tags} = values;
        const tagIds = tags.map(({id}) => id);
        // console.log(tagIds);
        return batchUpdateUserTags(selectUserId, tagIds).then(({data}) => {
            console.log(data);
            const {delErrors, newErrors} = data;
            if (!_.isEmpty(delErrors) || !_.isEmpty(newErrors)) {
                Modal.warn({
                    title: '批量更新标签时出现错误',
                    content: (
                        <>
                            {!_.isEmpty(delErrors) && (
                                <div>
                                    <p>删除已有标签出错：</p>
                                    <ul>
                                        {delErrors.map((tagName, index) => (
                                            <li key={index}>{tagName}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {!_.isEmpty(newErrors) && (
                                <div>
                                    <p>新增标签出错：</p>
                                    <ul>
                                        {newErrors.map((tagName, index) => (
                                            <li key={index}>{tagName}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    ),
                });
            }
            return true;
        });
    }

    function onDeleteClick(record) {
        const content = (
            <div>
                <p>确定离职 [{record.name}]？</p>
                <Divider />
            </div>
        );

        Modal.confirm({
            title: '警告',
            content,
            okButtonProps: {danger: true},
            onOk: () => {
                deleteUser(record.id).then(() => {
                    refreshTable();
                });
            },
        });
    }

    function onRealDeleteClick(record) {
        const content = (
            <div>
                <p>确定删除 [{record.name}]？</p>
                <Divider />
                <p>注意：由于企业微信的限制，强烈不建议删除用户</p>
                <p>否则想要再次恢复相同 email 的员工时将比较麻烦</p>
                <p>建议使用「用户修改」中的「禁用」功能</p>
            </div>
        );

        Modal.confirm({
            title: '警告',
            content,
            okButtonProps: {danger: true},
            onOk: () => {
                realDeleteUser(record.id).then(() => {
                    refreshTable();
                });
            },
        });
    }

    function refreshTable() {
        tableRef.current.refresh();
    }

    function onSearch(value) {
        console.log(value);
        // if (isDefaultAuth) {
        //     if (!value) return;
        // }
        setSearchFlag(true);
        let needDelValue = '';
        if (!isDefaultAuth) {
            if (needDel != '') needDelValue = needDel == '1' ? '需要删除' : '无需删除';
        }
        let statusText = '';
        if (status === 0) {
            statusText = '全部';
        } else if (status === 1) {
            statusText = '已激活';
        } else if (status === 2) {
            statusText = '已禁用';
        } else if (status === 4) {
            statusText = '未激活';
        } else {
            statusText = '退出企业';
        }
        setPageTitle(`查询：${statusText} ${value} ${needDelValue}`);
        if (isDefaultAuth) setUrl(getSearchUrl(depart.id, value, status));
        else setUrl(getSearchUrl(depart.id, value, status, needDel));
    }

    function onSearchBlur(e) {
        if (!e.target.value) {
            reset2Depart();
        }
    }

    function onRoleSelect(user) {
        console.log(user);
        roleSelRef.current.showModal(user.id, true);
    }

    function onRoleView(user) {
        console.log(user);
        roleSelRef.current.showModal(user.id, false);
    }

    function onResetPass(user) {
        resetPassModalRef.current.showModal(user);
    }

    // function onMenuClick({key}) {
    //     if (key === 'sync2Local') {
    //         syncDBRef.current.showModal();
    //     } else if (key === 'sync2Wecom') {
    //         syncWecomRef.current.showModal();
    //     }
    // }
    function onUserSelect(record) {
        const departIds = [];
        departIds.push(depart.id);

        setAllowDeparts(departIds);

        reportLineSelectorRef.current.showModal(record.id);
    }

    function onReportLineUserSelect(parentId, values) {
        const users = _.get(values, 'users', []);
        const groups = _.get(values, 'groups', []);
        const departs = _.get(values, 'departs', []);

        const subUserIds = users.map(({id}) => id);
        const groupUserIds = groups.map(({id}) => id);
        const departIds = departs.map(({id}) => id);

        console.log(2222, parentId, subUserIds, departIds);

        return addReportDepartsLine(parentId, subUserIds, departIds).then(data => {
            refreshTable();
            return data.status === 200;
        });
    }

    function renderExtra() {
        return (
            <Space>
                {!isDefaultAuth && (
                    <Select style={{width: '100px'}} value={needDel} onChange={e => setNeedDel(e)}>
                        <Option value="">默认</Option>
                        <Option value="1">需要删除</Option>
                        <Option value="0">无需删除</Option>
                    </Select>
                )}
                <Select
                    value={status}
                    onChange={e => {
                        setStatus(e);
                    }}
                >
                    <Option value={0}>全部</Option>
                    <Option value={1}>已激活</Option>
                    <Option value={2}>已禁用</Option>
                    <Option value={4}>未激活</Option>
                    <Option value={5}>退出企业</Option>
                </Select>
                <Input.Search
                    allowClear
                    loading={false}
                    onSearch={onSearch}
                    onBlur={onSearchBlur}
                    placeholder="按员工编号、姓名查询"
                    style={{width: 260}}
                />
                {editable && (
                    <Button type="primary" onClick={onAddClick}>
                        添加成员
                    </Button>
                )}
                {/*{editable*/}
                {/*    && (*/}
                {/*    <Button type="primary" onClick={sendBatchMessage}>*/}
                {/*    群发年会消息*/}
                {/*    </Button>*/}
                {/*    )*/}
                {/*}*/}
            </Space>
        );
    }

    const columns = getColumns(columnKeys, {onNameClick});
    if (editable) {
        let actions = [];
        let defaultActions = [
            {
                key: 'setRole',
                title: '管理范围',
                icon: <AuditOutlined />,
                hide: true,
                onClick: onUserSelect,
            },
            {
                key: 'divider',
                isDivider: true,
                hide: true,
            },
            {
                key: 'setRole',
                title: '赋角色',
                hide: true,
                icon: <FileProtectOutlined />,
                onClick: onRoleSelect,
            },
            {
                key: 'viewRoles',
                title: '查看角色',
                hide: true,
                icon: <AuditOutlined />,
                onClick: onRoleView,
            },
            {
                key: 'divider',
                isDivider: true,
                hide: true,
            },
            // {
            //     key: 'resetPass',
            //     title: '重置密码',
            //     hide: true,
            //     icon: <LockOutlined />,
            //     onClick: onResetPass,
            // },
            {
                key: 'divider1',
                isDivider: true,
                hide: true,
            },
            {
                key: 'modifyDeparts',
                title: '修改部门',
                hide: true,
                icon: <ApartmentOutlined />,
                onClick: onModifyDepartsClick,
            },
            // {
            //     key: 'modifyTags',
            //     title: '修改标签',
            //     hide: true,
            //     icon: <TagsOutlined />,
            //     onClick: onModifyTagsClick,
            // },
            {
                key: 'divider2',
                isDivider: true,
                hide: true,
            },
            {
                key: 'modify',
                title: '修改',
                hide: true,
                icon: <EditOutlined />,
                onClick: onModifyClick,
            },
            {
                key: 'delete',
                title: '离职删除',
                hide: true,
                icon: <DeleteOutlined />,
                onClick: onDeleteClick,
            },
            {
                key: 'realDelete',
                title: '彻底删除',
                hide: true,
                icon: <DeleteOutlined />,
                onClick: onRealDeleteClick,
            },
        ];
        let azureActions = [
            {
                key: 'divider',
                isDivider: true,
                hide: true,
            },
            {
                key: 'setRole',
                title: '管理范围',
                icon: <AuditOutlined />,
                hide: true,
                onClick: onUserSelect,
            },
        ];
        let delActions = [
            {
                key: 'delete',
                title: '删除',
                hide: true,
                icon: <DeleteOutlined />,
                onClick: onDeleteClick,
            },
        ];
        // actions = isDefaultAuth ? [...defaultActions] : [...azureActions];
        actions = [...defaultActions];
        columns.push({
            title: '操作',
            key: 'action',
            width: 100, // eslint-disable-next-line react/display-name
            render: (text, record) => {
                const {needDel} = record;
                let finalActions;
                // if (isAzureAuth) {
                finalActions = needDel ? [...actions] : [...actions];
                // } else finalActions = [...actions];

                return <RowAction actions={finalActions} record={record} />;
            },
        });
    }

    if (!editable && columnAction) {
        columns.push(columnAction);
    }

    return (
        <div style={{flex: 'auto', flexDirection: 'row'}}>
            <PageHeader title={pageTitle} extra={renderExtra()} />

            <div style={{margin: '0 0 24px'}}>
                {url && <PageGrid ref={tableRef} url={url} columns={columns} />}
            </div>

            {!_.isEmpty(depart) && (
                <UserFormModal ref={formModalRef} departId={depart.id} reloadUsers={refreshTable} />
            )}

            <UserViewModal ref={viewRef} />

            <RoleSelector ref={roleSelRef} scope="global" />

            <UserResetPassModal ref={resetPassModalRef} />

            <SyncContactToDBModal ref={syncDBRef} reloadUsers={refreshTable} />

            <SyncContactToWecomModal ref={syncWecomRef} />

            <MultiDepartEditModal
                ref={departEditModalRef}
                reloadUsers={searchFlag ? null : refreshTable}
            />

            <DepartUserTagSelector
                ref={dutSelectorRef}
                showDepartTab={false}
                showUserTab={false}
                showTagTab
                modalTitle="标签选择"
                onSelect={onTagsSelect}
            />
            <ReportLineUserSelector
                ref={reportLineSelectorRef}
                showTagTab={false}
                showDepartTab={false}
                modalTitle="数据权限"
                allowDepartIds={allowDeparts}
                onSelect={onReportLineUserSelect}
            />
        </div>
    );
}

UserList.propTypes = {
    depart: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
    }),
    editable: PropTypes.bool,
    columnKeys: PropTypes.arrayOf(PropTypes.string),
    columnAction: PropTypes.object,
    getSearchUrl: PropTypes.func,
};
